import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { FormBuilder, Validators } from '@angular/forms';
import { AuthService } from 'src/app/services/auth/auth.service';
import { ToastService } from 'src/app/services/toast/toast.service';
import { ApiService } from 'src/app/services/api/api.service';
import { StorageService } from 'src/app/services/storage/storage.service';
import {
  BreadcrumbService,
  BreadcrumbItem,
} from 'src/app/services/breadcrumb/breadcrumb.service';

type PermissionProps = {
  label: string;
  value: number;
  extra?: any;
};

type PermissionResponse = {
  description: string;
  id: number;
  slug: string;
  type: string;
  header: boolean;
  parent_permission: PermissionResponse | null;
};

@Component({
  selector: 'app-dashboard-permissions-list-register',
  templateUrl: './dashboard-permissions-list-register.component.html',
  styleUrls: ['./dashboard-permissions-list-register.component.scss'],
})
export class DashboardPermissionsListRegisterComponent implements OnInit {
  label: string = 'Adicionar conjunto de permissões';

  loading: boolean = true;

  form = this.formBuilder.group({
    name: [null, Validators.required],
    active: [true],
  });

  searchForm = this.formBuilder.group({
    avaliablePermissions: [null],
    selectedPermissions: [null],
  });

  user: NewLoginResponse | undefined;

  permissionTree: any[] = [];

  avaliableOrderAsc = true;
  selectedOrderAsc = true;
  isEditing = false;
  createdBy: string = '';
  permissions: SelectItem[] = [];

  totalAvaliableOptions = 0;
  avaliableCheckedNumber = 0;
  totalSelectedOptions = 0;
  selectedCheckedNumber = 0;

  permissionsSelectedPicked: PermissionProps[] = [];
  permissionsAvaliablePicked: PermissionProps[] = [];

  permissionsAvaliable: PermissionProps[] = [];
  permissionsSelected: PermissionProps[] = [];
  visiblePermissionsSelected: PermissionProps[] = [];
  permissionsBaseData: PermissionResponse[] = [];

  selectedPermissionTree: any[] = [];

  sendingRequest: boolean = false;
  allPermissionsFlat: any[] = [];

  // Modal de confirmação de saída
  showExitModal = false;
  hasUnsavedChanges = false;

  constructor(
    public route: Router,
    public formBuilder: FormBuilder,
    private authService: AuthService,
    private toast: ToastService,
    private api: ApiService,
    private breadcrumbService: BreadcrumbService
  ) {}

  ngOnInit(): void {
    if (!this.authService.verifyPermission(['de_can_add_permissions'])) {
      this.toast.show(
        'error',
        'Aviso',
        'Você não tem permissão para acessar essa página.'
      );
      this.route.navigateByUrl('/app/dashboard');
    }

    this.isEditing = this.route.url.includes('update');

    this.setBreadcrumb();

    this.user = this.authService.getUser();
    this.getData();

    this.label = this.route.url.includes('update')
      ? 'Atualizar conjunto de permissões'
      : 'Adicionar conjunto de permissões';

    // Detectar mudanças no formulário
    this.form.valueChanges.subscribe(() => {
      this.hasUnsavedChanges = true;
    });

    this.searchForm.valueChanges.subscribe(() => {
      this.hasUnsavedChanges = true;
    });

    this.searchForm.controls.avaliablePermissions.valueChanges.subscribe(
      async (value) => {
        this.permissionsAvaliablePicked = [];
        await this.getPermissionsOptions(value);

        this.filterPermissions();
        this.handleSetNumbers(true);
      }
    );

    this.searchForm.controls.selectedPermissions.valueChanges.subscribe(
      (value) => {
        this.permissionsSelectedPicked = [];

        this.filterSelectedPermissions(value);
        // this.visiblePermissionsSelected = this.permissionsSelected.filter(
        //   (item) => item.label.toLowerCase().includes(value.toLowerCase())
        // );
        this.handleSetNumbers();
      }
    );
  }

  handleCheckAll(isAvaliable: boolean = false) {
    if (isAvaliable) {
      const valuesMissingInPicked = this.permissionsAvaliable.filter(
        (item) =>
          !this.permissionsAvaliablePicked.some(
            (picked) => picked.value === item.value
          )
      );

      if (valuesMissingInPicked.length === 0) {
        this.permissionsAvaliablePicked = [];
        this.handleSetNumbers(true);
        return;
      }

      valuesMissingInPicked.forEach((item) => {
        this.permissionsAvaliablePicked.push(item);
      });
      this.handleSetNumbers(true);
    } else {
      const valuesMissingInPicked = this.visiblePermissionsSelected.filter(
        (item) =>
          !this.permissionsSelectedPicked.some(
            (picked) => picked.value === item.value
          )
      );

      if (valuesMissingInPicked.length === 0) {
        this.permissionsSelectedPicked = [];
        this.handleSetNumbers();
        return;
      }

      valuesMissingInPicked.forEach((item) => {
        this.permissionsSelectedPicked.push(item);
      });
      this.handleSetNumbers();
    }
  }

  verifyCheckAll(isAvaliable: boolean = false) {
    if (isAvaliable) {
      return (
        this.permissionsAvaliablePicked.length ===
        this.permissionsAvaliable.length
      );
    }
    return (
      this.permissionsSelectedPicked.length ===
      this.visiblePermissionsSelected.length
    );
  }

  filterPermissions() {
    const selectedIds = new Set(this.permissionsSelected.map((p) => p.value));

    // IDs disponíveis que não estão selecionados
    const stillAvailable = this.permissions.filter(
      (p) => !selectedIds.has(p.value)
    );

    // Novo array que inclui pais se ainda houver filhos disponíveis
    const effectiveAvailable: PermissionProps[] = [];

    const hasAvailableChild = (parentId: number): boolean => {
      return stillAvailable.some((p) => p.extra?.id === parentId);
    };

    for (const perm of this.permissions) {
      const isSelected = selectedIds.has(perm.value);
      const isParent = this.permissions.some((p) => p.extra?.id === perm.value);
      const hasChildStillAvailable = hasAvailableChild(perm.value);

      if (!isSelected || hasChildStillAvailable) {
        effectiveAvailable.push(perm);
      }
    }

    this.permissionsAvaliable = effectiveAvailable;

    // Reconstrói a árvore baseada nos disponíveis
    const flat = this.permissionsAvaliable.map((p) => ({
      id: p.value,
      description: p.label,
      type: p.label.split(' - ')[1],
      parent_permission: p.extra ?? null,
    }));

    this.permissionTree = this.buildPermissionTree(
      flat,
      flat.map((p) => p.id)
    );
  }

  filterSelectedPermissions(value: string): void {
    const search = value.toLowerCase();
    const flat = this.permissionsSelected.map((p) => {
      //   const [description, type] = p.label.split(' - ');
      return {
        id: p.value,
        description: p.label,
        type: p.label.split(' - ')[1],
        parent_permission: p.extra ?? null,
      };
    });

    const idsToKeep = new Set<number>();

    flat.forEach((perm) => {
      const matchesSearch =
        perm.description.toLowerCase().includes(search) ||
        perm.type.toLowerCase().includes(search);

      if (matchesSearch) {
        let current = perm;
        while (current) {
          idsToKeep.add(current.id);
          current = current.parent_permission;
        }
      }
    });

    const filtered = flat.filter((p) => idsToKeep.has(p.id));

    this.visiblePermissionsSelected = this.permissionsSelected.filter((p) =>
      idsToKeep.has(p.value)
    );

    this.selectedPermissionTree = this.buildPermissionTree(filtered);
  }

  async getData() {
    await this.getPermissionsOptions();
    if (this.isEditing) await this.getPermissionData();

    this.handleSetNumbers(true);
    this.handleSetNumbers();
    this.loading = false;
  }

  async getPermissionData() {
    try {
      const id = this.route.url.split('/').pop();
      const res: PermissionDetailsValue = await this.api.get({
        route: `api/v2/group/${id}/`,
        token: true,
      });

      this.form.controls.name.setValue(res.group);
      this.form.controls.active.setValue(res.status === 'Ativo');
      this.createdBy = res.created_by === '' ? 'Sistema' : res.created_by;

      this.permissionsSelected = res.permissions.map(
        (item: PermissionResponse) => {
          const findedParent = this.permissions.find(
            (i) => i.value === item.id
          ).extra;

          return {
            label: item.description,
            // label: item.description + ' - ' + item.type,
            value: item.id,
            extra: findedParent,
          };
        }
      );

      //   this.permissionsSelected.forEach((permission) => {
      // this.permissionsAvaliable = this.permissionsAvaliable.filter(
      //   (item) => item.value !== permission.value
      // );
      //   });

      this.updateSelectedPermissionTree();

      this.visiblePermissionsSelected = this.permissionsSelected;
    } catch (error) {
      this.toast.show('error', 'Erro', 'Erro ao carregar os dados');
    }
  }

  async getPermissionsOptions(params?: string) {
    try {
      let params = {
        search: this.searchForm.controls.avaliablePermissions.value,
      };

      //   if (!(this.storageService.getPermissionType() === 'owner'))
      //     params['register'] = this.user.active_register.register.id;

      const res = await this.api.get({
        route: 'api/v2/permission/',
        token: true,
        params,
      });

      const flat = res.map((item: PermissionResponse) => ({
        id: item.id,
        description: item.description,
        type: item.type,
        parent_permission: item.parent_permission,
      }));

      this.allPermissionsFlat = flat;
      this.permissionTree = this.buildPermissionTree(res);

      this.permissionsBaseData = res;

      this.permissions = res.map((item: PermissionResponse) => ({
        label: item.description,
        // label: item.description + ' - ' + item.type,
        value: item.id,
        extra: item.parent_permission,
      }));

      this.permissionsAvaliable = this.permissions;
    } catch (error) {
      this.toast.show('error', 'Erro', 'Erro ao buscar permissões');
    }
  }

  async submit() {
    try {
    } catch (error) {
      console.log(error);
    }
  }

  toggleValue(item: PermissionProps, isAvaliable: boolean = false) {
    if (isAvaliable) {
      if (this.isOptionsChecked(item, true)) {
        this.permissionsAvaliablePicked =
          this.permissionsAvaliablePicked.filter(
            (permission) => permission.value !== item.value
          );
      } else {
        this.permissionsAvaliablePicked.push(item);
      }

      this.checkAndSelectParentIfNeeded(item.value, true);

      this.handleSetNumbers(true);
    } else {
      if (this.isOptionsChecked(item, false)) {
        this.permissionsSelectedPicked = this.permissionsSelectedPicked.filter(
          (permission) => permission.value !== item.value
        );
      } else {
        this.permissionsSelectedPicked.push(item);
      }

      this.checkAndSelectParentIfNeeded(item.value, false);

      //   this.handleSetNumbers();
      this.cleanOrphanedParents();
      this.handleSetNumbers();
    }
  }

  isOptionsChecked(item: PermissionProps, isAvaliable: boolean = false) {
    if (isAvaliable) {
      return this.permissionsAvaliablePicked.some(
        (permission) => permission.value === item.value
      );
    }
    return this.permissionsSelectedPicked.some(
      (permission) => permission.value === item.value
    );
  }

  getDisabled(isAvaliable: boolean = false) {
    return isAvaliable
      ? this.permissionsAvaliablePicked.length === 0
      : this.permissionsSelectedPicked.length === 0;
  }

  changeSide(isAvaliable: boolean = false) {
    // verifica se o lado é o esquerdo (disponível)
    if (isAvaliable) {
      // move cada permissão selecionada do lado esquerdo para o direito
      this.permissionsAvaliablePicked.forEach((permission) => {
        // adiciona na lista de selecionadas
        // this.permissionsSelected.push(permission);
        this.addPermissionWithParents(permission);
        // adiciona na lista visível (após filtros)
        this.visiblePermissionsSelected.push(permission);

        // remove a permissão da lista de disponíveis
        this.permissionsAvaliable = this.permissionsAvaliable.filter(
          (item) => item.value !== permission.value
        );
      });

      // remove duplicadas da lista de selecionadas
      this.permissionsSelected = this.permissionsSelected.filter(
        (item, index, self) =>
          index === self.findIndex((t) => t.value === item.value)
      );

      // remove duplicadas da lista visível de selecionadas
      this.visiblePermissionsSelected = this.visiblePermissionsSelected.filter(
        (item, index, self) =>
          index === self.findIndex((t) => t.value === item.value)
      );

      // reaplica o filtro de busca das permissões selecionadas
      const searchFormValue =
        this.searchForm.controls.selectedPermissions.value;

      if (searchFormValue)
        this.searchForm.controls.selectedPermissions.setValue(searchFormValue);

      // limpa as permissões marcadas no lado esquerdo
      this.permissionsAvaliablePicked = [];
    } else {
      // move as permissões marcadas do lado direito para o esquerdo
      this.permissionsSelectedPicked.forEach((permission) => {
        // remove da lista de selecionadas
        this.permissionsSelected = this.permissionsSelected.filter(
          (item) => item.value !== permission.value
        );

        // remove da lista visível de selecionadas
        this.visiblePermissionsSelected =
          this.visiblePermissionsSelected.filter(
            (item) => item.value !== permission.value
          );
      });

      // limpa as permissões marcadas do lado direito
      this.permissionsSelectedPicked = [];

      // reaplica o filtro de busca das permissões disponíveis
      const searchFormValue =
        this.searchForm.controls.avaliablePermissions.value;
      this.searchForm.controls.avaliablePermissions.setValue(searchFormValue);
    }

    // atualiza a árvore de permissões
    this.updateSelectedPermissionTree();
    // filtra as permissões disponíveis com base nas selecionadas
    this.filterPermissions();
    // atualiza contadores de seleção
    this.handleSetNumbers(true);
    this.handleSetNumbers();
  }

  getSubmitDisabled() {
    return (
      this.form.invalid ||
      this.sendingRequest ||
      this.permissionsSelected.length === 0
    );
  }

  handleSetNumbers(isAvaliable: boolean = false) {
    if (isAvaliable) {
      this.totalAvaliableOptions = this.permissionsAvaliable.length;
      this.avaliableCheckedNumber = this.permissionsAvaliablePicked.length;
    } else {
      this.totalSelectedOptions = this.visiblePermissionsSelected.length;
      this.selectedCheckedNumber = this.permissionsSelectedPicked.length;
    }
  }

  async handleAddPermission() {
    this.sendingRequest = true;
    try {
      const payload = {
        name: this.form.controls.name.value,
        permissions: this.permissionsSelected.map(
          (permission) => permission.value
        ),
        status: this.form.controls.active.value,
      };

      this.isEditing
        ? await this.api.put({
            route: `api/v2/group/${this.route.url.split('/').pop()}/`,
            body: payload,
            token: true,
          })
        : await this.api.post({
            route: 'api/v2/group/',
            body: payload,
            token: true,
          });

      this.toast.show(
        'info',
        'Sucesso',
        'Grupo de permissão adicionado com sucesso'
      );
      this.hasUnsavedChanges = false;
      this.route.navigateByUrl('app/permissions/list');
    } catch (error) {
      this.toast.show('error', 'Erro', 'Erro ao adicionar grupo de permissão');
    }

    this.sendingRequest = false;
  }

  handleCancel() {
    if (this.hasUnsavedChanges) {
      this.showExitModal = true;
    } else {
      this.route.navigateByUrl('app/permissions/list');
    }
  }

  // Métodos para o modal de confirmação de saída
  confirmExit() {
    this.showExitModal = false;
    this.hasUnsavedChanges = false;
    this.route.navigateByUrl('app/permissions/list');
  }

  cancelExit() {
    this.showExitModal = false;
  }

  buildPermissionTree(flatList: any[], availableIds: number[] = []): any[] {
    const lookup: Record<number, any> = {};
    const tree: any[] = [];

    flatList.forEach((item) => {
      lookup[item.id] = { ...item, children: [] };
    });

    flatList.forEach((item) => {
      const parent = item.parent_permission;
      if (parent && lookup[parent.id]) {
        lookup[parent.id].children.push(lookup[item.id]);
      } else {
        tree.push(lookup[item.id]);
      }
    });

    const pruneTree = (nodes: any[]): any[] => {
      return nodes
        .map((node) => {
          const prunedChildren = pruneTree(node.children || []);
          const isInOriginal = flatList.some((item) => item.id === node.id);
          if (prunedChildren.length > 0 || isInOriginal) {
            return {
              ...node,
              children: prunedChildren,
            };
          }
          return null;
        })
        .filter((node) => node !== null);
    };

    return pruneTree(tree);
  }

  handlePermissionSelection(id: number): void {
    const item = this.findPermissionById(id, [
      ...this.permissionTree,
      ...this.selectedPermissionTree,
    ]);
    if (item) {
      const permissionToToggle: PermissionProps = {
        label: item.description,
        value: item.id,
        extra: item.parent_permission ?? null,
      };

      const isAvaliable = this.permissionsAvaliable.some(
        (p) => p.value === item.id
      );
      this.toggleValue(permissionToToggle, isAvaliable);
    }
  }

  updateSelectedPermissionTree(): void {
    // Cria um mapa para armazenar as permissões com estrutura de árvore (sem duplicar nós)
    const map = new Map<number, any>();

    // Função auxiliar para buscar uma permissão completa a partir do ID
    const findFullPermissionById = (id: number): any => {
      // Procura a permissão na lista original usando o ID
      const found = this.permissions.find((p) => p.value === id);
      if (found) {
        // Retorna um objeto completo com os dados necessários
        return {
          id: found.value,
          description: found.label,
          type: found.label.split(' - ')[1],
          parent_permission: found.extra ?? null, // Pode ou não ter pai
        };
      }
      return null; // Se não encontrar, retorna null
    };

    // Função recursiva para subir na hierarquia e montar a árvore
    const buildTreeUp = (node: any) => {
      // Evita adicionar o mesmo nó mais de uma vez
      if (!map.has(node.id)) {
        // Adiciona o nó atual ao mapa
        map.set(node.id, {
          id: node.id,
          description: node.description,
          type: node.type,
          parent_permission: node.parent_permission,
          children: [], // Inicializa filhos vazios
        });

        // Se tiver pai, busca o pai completo e continua a recursão
        if (node.parent_permission) {
          const fullParent = findFullPermissionById(node.parent_permission.id);
          if (fullParent) {
            buildTreeUp(fullParent); // Recursão: sobe para o pai
          }
        }
      }
    };

    // Itera por todas as permissões selecionadas pelo usuário (lado direito)
    for (const perm of this.permissionsSelected) {
      // Monta um objeto com os dados da permissão selecionada
      const fullNode = {
        id: perm.value,
        description: perm.label,
        type: perm.label.split(' - ')[1],
        parent_permission: perm.extra ?? null,
      };

      // Constrói a árvore a partir desse nó, incluindo os pais
      buildTreeUp(fullNode);
    }

    // Prepara um objeto auxiliar para facilitar a montagem da árvore com filhos
    const lookup: { [key: number]: any } = {};
    map.forEach((value, key) => {
      lookup[key] = { ...value, children: [] }; // Clona cada nó e inicializa filhos
    });

    const tree: any[] = [];

    // Monta a estrutura de árvore com base nas relações de pai-filho
    for (const item of map.values()) {
      if (item.parent_permission && lookup[item.parent_permission.id]) {
        // Se tem pai, adiciona como filho do pai
        lookup[item.parent_permission.id].children.push(lookup[item.id]);
      } else {
        // Se não tem pai, é um nó raiz
        tree.push(lookup[item.id]);
      }
    }

    // Atribui a árvore final para exibição no lado direito (permissões selecionadas)
    this.selectedPermissionTree = tree;

    // Atualiza as permissões do lado esquerdo, removendo o que está do lado direito
    this.filterPermissions();
  }

  findPermissionById(id: number, list: any[]): any | null {
    for (const item of list) {
      if (item.id === id) return item;
      if (item.children) {
        const found = this.findPermissionById(id, item.children);
        if (found) return found;
      }
    }
    return null;
  }

  cleanOrphanedParents(): void {
    const selectedIds = new Set(this.permissionsSelected.map((p) => p.value));
    const allWithParents = [...this.permissionsSelected];

    let changed: boolean;

    do {
      changed = false;

      for (let i = allWithParents.length - 1; i >= 0; i--) {
        const current = allWithParents[i];
        const parentId =
          current.extra?.id || current.extra?.parent_permission?.id;

        if (parentId && !selectedIds.has(parentId)) {
          const hasSibling = allWithParents.some(
            (p) =>
              p.extra?.id === parentId ||
              p.extra?.parent_permission?.id === parentId
          );

          if (!hasSibling) {
            const index = allWithParents.findIndex((p) => p.value === parentId);
            if (index !== -1) {
              selectedIds.delete(parentId);
              allWithParents.splice(index, 1);
              changed = true;
            }
          }
        }
      }
    } while (changed);

    this.permissionsSelected = [...allWithParents];
    this.visiblePermissionsSelected = [...allWithParents];
    // this.updateSelectedPermissionTree();
  }

  checkAndSelectParentIfNeeded(
    nodeId: number,
    isAvaliable: boolean = false
  ): void {
    const flatList = isAvaliable
      ? this.permissionsAvaliable
      : this.visiblePermissionsSelected;

    const selectedList = isAvaliable
      ? this.permissionsAvaliablePicked
      : this.permissionsSelectedPicked;

    const item = flatList.find((p) => p.value === nodeId);
    if (!item || !item.extra) return;

    const parentId = item.extra.id;
    const siblings = flatList.filter((p) => p.extra?.id === parentId);

    const allSiblingsChecked = siblings.every((sibling) =>
      selectedList.some((p) => p.value === sibling.value)
    );

    const parentIsChecked = selectedList.some((p) => p.value === parentId);
    const fullParent = this.permissions.find((p) => p.value === parentId);

    if (!fullParent) return;

    if (allSiblingsChecked && !parentIsChecked) {
      this.toggleValue(fullParent, isAvaliable);
    }

    if (!allSiblingsChecked && parentIsChecked) {
      this.toggleValue(fullParent, isAvaliable);
    }

    this.checkAndSelectParentIfNeeded(parentId, isAvaliable);
  }

  addPermissionWithParents(permission: PermissionProps): void {
    // Evita duplicação
    if (!this.permissionsSelected.some((p) => p.value === permission.value)) {
      this.permissionsSelected.push(permission);
    }

    // Adiciona o pai, se existir
    const parent = permission.extra;
    if (
      parent &&
      !this.permissionsSelected.some((p) => p.value === parent.id)
    ) {
      const fullParent = this.permissions.find((p) => p.value === parent.id);
      if (fullParent) {
        this.addPermissionWithParents(fullParent);
      }
    }
  }

  setBreadcrumb(): void {
    const canViewPermissions = this.authService.verifyPermission(
      'de_can_view_permissions'
    );
    const canViewList = this.authService.verifyPermission(
      'de_can_view_permissions'
    );
    const isNew = this.route.url.includes('new');
    const breadcrumbs: BreadcrumbItem[] = [
      {
        label: 'Permissões',
        url: '/app/permissions',
        clickable: canViewPermissions,
      },
      {
        label: 'Controle de permissões',
        url: '/app/permissions/list',
        clickable: canViewList,
      },
      {
        label: isNew
          ? 'Adicionar conjunto de permissões'
          : 'Atualizar conjunto de permissões',
        url: this.route.url,
        clickable: false, // Página atual, não precisa ser clicável
      },
    ];
    this.breadcrumbService.setBreadcrumbs(breadcrumbs);
  }
}
